import React, {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useState
} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
  clearIncidents,
  getIncidentsCount,
  searchIncidents
} from "store/features/incidentsSlice";
import {selectAllIncidentSettings, selectResetIncidents} from "store/selectors";
import {
  DEFAULT_PAGE,
  DEFAULT_RESULTS_PER_PAGE,
  INCIDENT_FILTER_SORT_DEFAULT
} from "models/options";

const IncidentsContext = createContext({
  page: DEFAULT_PAGE,
  pageCount: DEFAULT_RESULTS_PER_PAGE,
  filterSettings: {
    assigneeFilter: [],
    statusFilter: [],
    order: "",
    orderBy: "",
    dateRangeFilter: {}
  },
  searchQuery: "",
  setPage: () => {},
  setPageCount: () => {},
  dispatchFilterSettings: () => {},
  handleSearchTextChange: () => {},
  handleFilterDropdownMenuChange: () => {},
  handleMetricsCardSearch: () => {}
});

const IncidentsProvider = ({children}) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const reset = useSelector(selectResetIncidents);
  const incidentFilters = useSelector(selectAllIncidentSettings);
  const initialFilters =
    incidentFilters !== null ? incidentFilters : INCIDENT_FILTER_SORT_DEFAULT;

  /**
   * Value of initialFilters on first render will be either:
   * 1) Settings loaded from LocalStorage/Registry, if they exist, or,
   * 2) Default Settings
   *
   * Initialize the Reducers state with initialFilters (and default values for
   * page/paceCount which are not stored with other settings)
   *
   * Calls to "dispatchFilterSettings" can accept an object as parameter.
   * The object can be an entirely new state or a subset of state values which
   * will be merged with the existing state to produce a new state object.
   *
   * This method allows multiple state values to be updated at once, instead of
   * tracking each one as individual piece of state.
   *
   * NOTE: This Reducer hook tracks all state values used by the Incidents page
   * and it's components. It is initially populated, in part, by the Redux
   * IncidentsSettings store. The Redux store is tracks only the Incidents
   * Settings as they are stored in LocalStorage/Registry.
   *
   * This is an important distinction as to how each is used in this context.
   */
  const [filterSettings, dispatchFilterSettings] = useReducer(
    (state, action) => ({...state, ...action}),
    {
      order: initialFilters.order,
      orderBy: initialFilters.orderBy,
      assigneeFilter: initialFilters.assigneeFilter,
      statusFilter: initialFilters.statusFilter,
      dateRangeFilter: initialFilters.dateRangeFilter,
      page: DEFAULT_PAGE,
      pageCount: DEFAULT_RESULTS_PER_PAGE
    }
  );

  useEffect(() => {
    dispatch(getIncidentsCount());
    return () => dispatch(clearIncidents());
  }, [dispatch]);

  /**
   * Serialize settings to use as useEffect dependency.
   * Protects against issues when non-primitive values in state change.
   */
  const settingsString = JSON.stringify(filterSettings);

  // Perform a new Incident Search using currect state of filterSettings.
  const performIncidentSearch = useCallback(() => {
    dispatch(
      searchIncidents({
        ...filterSettings,
        searchText: searchQuery
      })
    );
  }, [dispatch, settingsString, searchQuery]);

  /**
   * Triggers a new Search for Incidents when searchQuery or any
   * filterSettings change value.
   */
  useEffect(() => {
    performIncidentSearch();
  }, [performIncidentSearch, settingsString, searchQuery]);

  /**
   * Special Case:
   * If the "reset" state value is set to "true" in Redux store, then trigger
   * a new Incident Search. Triggering a search, changes the stored value of
   * 'reset' to 'false' in the Redux store so this action is not repeated.
   *
   * This currently occurs during a Bulk Delete of Incidents:
   * To allow backend to propagate deletion, we only search the updated Incident
   * List post-deletion when this trigger occurs and not automatically on
   * success of the delete action. (See IncidentsSagas to impl.)
   */
  useEffect(() => {
    // Re-run search if reset prop has been set to true.
    if (reset === true) {
      performIncidentSearch();
    }
  }, [reset]);

  /**
   * Helper Functions:
   *
   * The functions below are provided to components consuming this Context.
   * Some components need to update multiple state values at once,
   * to handle events.
   *
   * Combining these state updates into a single helper function:
   * 1) Cleaner to read code. It is clear which state values change and when.
   * 2) Reduce number of state updates and at what layer they are called.
   * 3) Takes advantage of Reacts state update "batching".
   * React will batch multiple state updates made in a single Event handler.
   * This prevents multiple re-renders which prevents multiple calls to saerch
   * API when state changes.
   *
   * *** When to implement new handler: ***
   * If your component has an event Handler which updates multiple state value,
   * and/or has side effects of needing to update another state value
   * (such as resetting a page value to default), implement a new handler
   * below to update all state inside one callback function.
   */

  /**
   * Handles new search input. Set the string value of the searchText and also
   * reset the page value to default (page: 1)
   * @param {String} searchText
   */
  const handleSearchTextChange = (searchText) => {
    setPage(DEFAULT_PAGE);
    setSearchQuery(searchText);
  };

  /**
   * Handler for changes made by Filter Menu Dropdown.
   * Filters object will contain new assigneeFilter and/or statusFilter.
   * This also requires the page value to be reset to default (page: 1).
   * @param {Object} filters {assigneeFilter, statusFilter}
   */
  const handleFilterDropdownMenuChange = (filters) => {
    dispatchFilterSettings(filters);
    setPage(DEFAULT_PAGE);
  };

  /**
   * Metrics cards perform a predefined search. The filter object will
   * contain an updated value for most filterSettings.
   * Page value and searchText must also be reset to default.
   * Only pageCount retains its existing value.
   * @param {Object} filters
   */
  const handleMetricsCardSearch = (filters) => {
    setSearchQuery("");
    dispatchFilterSettings(filters);
    setPage(DEFAULT_PAGE);
  };

  /**
   * Helper function. Only updates page value.
   * @param {number} page
   */
  const setPage = (page) => dispatchFilterSettings({page});

  /**
   * Helper function. Only updates pageCount value.
   * @param {number} results
   */
  const setPageCount = (results) =>
    dispatchFilterSettings({pageCount: results});

  return (
    <IncidentsContext.Provider
      value={{
        pageCount: filterSettings.pageCount,
        page: filterSettings.page,
        filterSettings,
        searchQuery,
        setPage,
        setPageCount,
        dispatchFilterSettings,
        handleSearchTextChange,
        handleFilterDropdownMenuChange,
        handleMetricsCardSearch
      }}>
      {children}
    </IncidentsContext.Provider>
  );
};

export {IncidentsContext, IncidentsProvider};
